import { getStoreType } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getStoreType";
import { IOffer } from "@faire/web-api--source/indigofair/data/IOffer";
import { MediumFormatting } from "@faire/web/common/localization/formatting";
import { StoreType } from "@faire/web/common/StoreType";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

type IOptions = {
  formattedIncentivePercentNumber: string;
  offerName?: keyof typeof IOffer.RetailerOfferName;
  storeType?: StoreType;
  isMaximalistFoi?: boolean;
  isFoiForFD?: boolean;
};

export const getVerticalizedTopBannerText = ({
  formattedIncentivePercentNumber,
  offerName,
  storeType = getStoreType(),
  isMaximalistFoi,
  isFoiForFD,
}: IOptions): React.JSX.Element => {
  if (isFoiForFD) {
    return getFoiForFaireDirectVerticalizedTopBannerText({
      formattedIncentivePercentNumber,
      offerName,
      storeType,
    });
  }

  if (isMaximalistFoi) {
    return getMaximalistFoiVerticalizedTopBannerText({
      formattedIncentivePercentNumber,
      offerName,
      storeType,
    });
  }

  switch (storeType) {
    case StoreType.GIFT:
    case StoreType.HOME_DECOR:
    case StoreType.FURNITURE:
      return (
        <StrictLocalMsg
          id="J+YNjw"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off!</MediumFormatting> Shop seasonal decor, home accents, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to gift, home decor, and furniture stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.BOOK:
      return (
        <StrictLocalMsg
          id="ABboeU"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off!</MediumFormatting> Shop books, journals, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to book stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.SPA_OR_SALON:
      return (
        <StrictLocalMsg
          id="0jIYXt"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off!</MediumFormatting> Shop beauty, wellness, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to spa and salon stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.PET:
      return (
        <StrictLocalMsg
          id="tEYwCK"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off!</MediumFormatting> Shop pet treats, toys, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to pet stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.FLORIST_OR_GARDEN:
      return (
        <StrictLocalMsg
          id="sEg0TS"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off!</MediumFormatting> Shop flowers, vases, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to florist and garden stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.KIDS_OR_TOY:
      return (
        <StrictLocalMsg
          id="vc326b"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off!</MediumFormatting> Shop toys, games, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to kids and toy stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.FITNESS_OR_YOGA:
      return (
        <StrictLocalMsg
          id="R+7qoY"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off!</MediumFormatting> Shop yoga, fitness, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to fitness and yoga stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.CLOTHING:
      return (
        <StrictLocalMsg
          id="pkwKlI"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off!</MediumFormatting> Shop apparel, accessories, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to clothing stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.CAFE_OR_RESTAURANT:
    case StoreType.GROCERY_OR_LIQUOR:
      return (
        <StrictLocalMsg
          id="hnKcgb"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off!</MediumFormatting> Shop food, drinks, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to cafe, restaurant, grocery, and liquor stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.OTHER:
    case StoreType.GENERAL:
    default:
      return getGenericTopBannerText({
        formattedIncentivePercentNumber,
        offerName,
      });
  }
};

const getMaximalistFoiVerticalizedTopBannerText = ({
  formattedIncentivePercentNumber,
  offerName,
  storeType = getStoreType(),
}: {
  formattedIncentivePercentNumber: string;
  offerName?: keyof typeof IOffer.RetailerOfferName;
  storeType?: StoreType;
}): React.JSX.Element => {
  switch (storeType) {
    case StoreType.GIFT:
    case StoreType.HOME_DECOR:
    case StoreType.FURNITURE:
      return (
        <StrictLocalMsg
          id="UeBYf/"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off and free shipping!</MediumFormatting> Shop seasonal decor, home accents, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to gift, home decor, and furniture stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.BOOK:
      return (
        <StrictLocalMsg
          id="yMZBlP"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off and free shipping!</MediumFormatting> Shop books, journals, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to book stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.SPA_OR_SALON:
      return (
        <StrictLocalMsg
          id="QNHtQc"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off and free shipping!</MediumFormatting> Shop beauty, wellness, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to spa and salon stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.PET:
      return (
        <StrictLocalMsg
          id="rAnJi4"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off and free shipping!</MediumFormatting> Shop pet treats, toys, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to pet stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.FLORIST_OR_GARDEN:
      return (
        <StrictLocalMsg
          id="2EIvhW"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off and free shipping!</MediumFormatting> Shop flowers, vases, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to florist and garden stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.KIDS_OR_TOY:
      return (
        <StrictLocalMsg
          id="xupyz+"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off and free shipping!</MediumFormatting> Shop toys, games, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to kids and toy stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.FITNESS_OR_YOGA:
      return (
        <StrictLocalMsg
          id="lwYALu"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off and free shipping!</MediumFormatting> Shop yoga, fitness, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to fitness and yoga stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.CLOTHING:
      return (
        <StrictLocalMsg
          id="KLKpYg"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off and free shipping!</MediumFormatting> Shop apparel, accessories, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to clothing stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.CAFE_OR_RESTAURANT:
    case StoreType.GROCERY_OR_LIQUOR:
      return (
        <StrictLocalMsg
          id="SrtsUV"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off and free shipping!</MediumFormatting> Shop food, drinks, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to cafe, restaurant, grocery, and liquor stores.",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.OTHER:
    case StoreType.GENERAL:
    default:
      return getGenericTopBannerText({
        formattedIncentivePercentNumber,
        offerName,
        isMaximalistFoi: true,
      });
  }
};

const getFoiForFaireDirectVerticalizedTopBannerText = ({
  formattedIncentivePercentNumber,
  offerName,
  storeType = getStoreType(),
}: {
  formattedIncentivePercentNumber: string;
  offerName?: keyof typeof IOffer.RetailerOfferName;
  storeType?: StoreType;
}): React.JSX.Element => {
  switch (storeType) {
    case StoreType.GIFT:
    case StoreType.HOME_DECOR:
    case StoreType.FURNITURE:
      return (
        <StrictLocalMsg
          id="K5L+eS"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off with any brand!</MediumFormatting> Shop seasonal decor, home accents, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to gift, home decor, and furniture stores.",
            img: "https://cdn.faire.com/fastly/89b9f621207ffa05ca5ee8dcdcb036b72a9a538a758aa23f309a6c6bb42b00ed.png",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.BOOK:
      return (
        <StrictLocalMsg
          id="lbdZUG"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off with any brand!</MediumFormatting> Shop books, journals, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to book stores.",
            img: "https://cdn.faire.com/fastly/d8cac1ed47085365b8bcd22bf6b75bebd76386cccb600ede1aa7a60299cf51dd.png",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.SPA_OR_SALON:
      return (
        <StrictLocalMsg
          id="GZQ3Ik"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off with any brand!</MediumFormatting> Shop beauty, wellness, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to spa and salon stores.",
            img: "https://cdn.faire.com/fastly/79cccf9d8f86ee2c99dabd356cf603cae689d2c59da02d8e49d7bef077f3b133.png",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.PET:
      return (
        <StrictLocalMsg
          id="cm0Yol"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off with any brand!</MediumFormatting> Shop pet treats, toys, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to pet stores.",
            img: "https://cdn.faire.com/fastly/65909157733ef43baa6ca1cb14f18831ec4dff1b1737819abbabea98fecefdbe.png",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.FLORIST_OR_GARDEN:
      return (
        <StrictLocalMsg
          id="4rbf8L"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off with any brand!</MediumFormatting> Shop flowers, vases, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to florist and garden stores.",
            img: "https://cdn.faire.com/fastly/2a19c85769f0a2167734da27d22a25c645d180329cb68e3279986cc7f4dc66d3.png",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.KIDS_OR_TOY:
      return (
        <StrictLocalMsg
          id="s+6LS/"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off with any brand!</MediumFormatting> Shop toys, games, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to kids and toy stores.",
            img: "https://cdn.faire.com/fastly/bace953ea35ba19a0eb4f9ad1fc3a5594e66bb4b52157a5c4c852f0a4dd3dc28.png",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.FITNESS_OR_YOGA:
      return (
        <StrictLocalMsg
          id="sVsSv4"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off with any brand!</MediumFormatting> Shop yoga, fitness, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to fitness and yoga stores.",
            img: "https://cdn.faire.com/fastly/fc71f6e1d31d9cf7c656a9d2c180dd4b4010fdb2068bec0d42e63f998d6bba18.png",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.CLOTHING:
      return (
        <StrictLocalMsg
          id="9ih3mQ"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off with any brand!</MediumFormatting> Shop apparel, accessories, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to clothing stores.",
            img: "https://cdn.faire.com/fastly/ea96fbfb9e62eff45986098081731a7f5b64a93342fe924b8878407efe1c0be2.png",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.CAFE_OR_RESTAURANT:
    case StoreType.GROCERY_OR_LIQUOR:
      return (
        <StrictLocalMsg
          id="oklUEK"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off with any brand!</MediumFormatting> Shop food, drinks, and more."
          description={{
            text: "Text letting the user know they have a discount available to them. specific to cafe, restaurant, grocery, and liquor stores.",
            img: "https://cdn.faire.com/fastly/375e591de44462fd4153ff035046143d26a6e207a9edcf5b4e6e4b39f52aa9a0.png",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
    case StoreType.OTHER:
    case StoreType.GENERAL:
    default:
      return getGenericTopBannerText({
        formattedIncentivePercentNumber,
        offerName,
        isFoiForFD: true,
      });
  }
};

const getGenericTopBannerText = ({
  formattedIncentivePercentNumber,
  isFoiForFD,
  isMaximalistFoi,
  offerName,
}: IOptions) => {
  switch (offerName) {
    case IOffer.RetailerOfferName.FAIRE_DIRECT_MARKETPLACE_DISCOUNT:
    case IOffer.RetailerOfferName.EVERGREEN_MARKETPLACE_DISCOUNT:
      return (
        <StrictLocalMsg
          id="/r0SRp"
          defaultMessage="<BoldFormatting>Enjoy {promoMoney} off</BoldFormatting> your next order with any new brand!"
          description={{
            text: "Content of Faire Direct Marketplace banner highlighting the marketplace incentive.",
            img: "https://cdn.faire.com/fastly/6b2f577fbe580fcd4c44f08402290157df9640931c646828959846d9da580926.png",
          }}
          values={{
            BoldFormatting: MediumFormatting,
            promoMoney: formattedIncentivePercentNumber,
          }}
        />
      );
    default:
      if (isFoiForFD) {
        return (
          <StrictLocalMsg
            id="zt5ms/"
            defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off with any brand!</MediumFormatting> Shop bestsellers, eco-friendly products, and more."
            description={{
              text: "Text letting the user know they have a discount available to them.",
              img: "https://cdn.faire.com/fastly/89daa423fc23d9ae5574ca9544991b41182d1d7886b51a147feceecfddc31db1.png",
            }}
            values={{
              MediumFormatting,
              formattedIncentivePercentNumber,
            }}
          />
        );
      }

      if (isMaximalistFoi) {
        return (
          <StrictLocalMsg
            id="XQOVyJ"
            defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off and free shipping!</MediumFormatting> Shop bestsellers, eco-friendly products, and more."
            description={{
              text: "Text letting the user know they have a discount available to them.",
              img: "https://cdn.faire.com/fastly/bed0ee9c9a080fe6709713876929af350f434ec34f2362b7e77d340814f2b0d7.png",
            }}
            values={{
              MediumFormatting,
              formattedIncentivePercentNumber,
            }}
          />
        );
      }

      return (
        <StrictLocalMsg
          id="H66dl7"
          defaultMessage="<MediumFormatting>Enjoy {formattedIncentivePercentNumber} off!</MediumFormatting> Shop bestsellers, eco-friendly products, and more."
          description={{
            text: "Text letting the user know they have a discount available to them.",
            img: "https://cdn.faire.com/fastly/bed0ee9c9a080fe6709713876929af350f434ec34f2362b7e77d340814f2b0d7.png",
          }}
          values={{
            MediumFormatting,
            formattedIncentivePercentNumber,
          }}
        />
      );
  }
};
